import { useCallback, useEffect, useRef, useState } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { money, errMsg } from "@/lib/api";
import { play, soundForTransition, PRIORITY } from "@/lib/sound";

const COLS = [
  ["awaiting_approval", "Awaiting approval", "bg-white border-2 border-dashed border-brand", "Approve"],
  ["paid", "Paid", "bg-brand-pale", "Start preparing"],
  ["preparing", "Preparing", "bg-brand-light", "Mark ready"],
  ["ready", "Ready", "bg-ready-pale", "Picked up"],
];
const PAY_METHODS = [["cash", "Cash"], ["card_terminal", "Card (own terminal)"], ["etransfer", "e-Transfer"]];

const mins = (iso) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));

const ManualActions = ({ o, act }) => {
  if (o.status === "awaiting_approval") return (
    <div className="mt-3 flex gap-2">
      <button onClick={() => act(o, "approve")} className="flex-1 rounded-full bg-brand py-3 font-heading font-black text-white" data-testid={`approve-order-${o.number}`}>Approve</button>
      <button onClick={() => act(o, "reject")} className="rounded-full border-2 border-line px-4 text-sm font-bold text-danger" data-testid={`reject-order-${o.number}`}>Reject</button>
    </div>);
  return (
    <div className="mt-3 flex gap-2">
      {o.status === "preparing" && <button onClick={() => act(o, "advance")} className="flex-1 rounded-full bg-ready py-3 font-heading font-black text-ink" data-testid={`advance-order-${o.number}`}>Mark ready</button>}
      {o.status === "ready" && <button onClick={() => act(o, "pickup")} className="flex-1 rounded-full bg-brand py-3 font-heading font-black text-white" data-testid={`pickup-order-${o.number}`}>Paid and picked up</button>}
      <button onClick={() => act(o, "no-show")} className="rounded-full border-2 border-line px-3 text-sm font-bold" data-testid={`noshow-order-${o.number}`}>No-show</button>
    </div>);
};

const needsAck = (o) => ["paid", "awaiting_approval"].includes(o.status) && !o.acknowledged_at;

const AckButton = ({ o, onAck }) => (
  <button onClick={() => onAck(o)} data-testid={`ack-order-${o.number}`}
    className="mb-3 flex w-full animate-pulse items-center justify-center gap-2 rounded-xl bg-ready py-3 font-heading font-black text-ink">
    <BellRing size={18} /> New! Tap to acknowledge
  </button>
);

const OrderCard = ({ o, action, onAdvance, onCancel, act, onAck }) => (
  <div className={`rounded-2xl border-2 bg-white p-4 ${o.status === "ready" || needsAck(o) ? "border-ready" : "border-line"}`} data-testid={`board-order-${o.number}`} data-acknowledged={!needsAck(o)}>
    {needsAck(o) && <AckButton o={o} onAck={onAck} />}
    <div className="flex items-start justify-between">
      <div><div className="font-heading text-3xl font-black">{o.number}{o.practice && <span className="ml-2 rounded-full border-2 border-dashed border-ink px-2 align-middle text-xs" data-testid={`board-practice-${o.number}`}>PRACTICE</span>}</div><div className="font-semibold">{o.customer_name}</div></div>
      <div className="text-right text-xs text-muted2">{mins(o.paid_at || o.created_at)} min ago<br />{money(o.totals.total_cents)}</div>
    </div>
    <ul className="mt-2 space-y-0.5 text-sm">{o.items.map((i, k) => <li key={k}><b>{i.qty}×</b> {i.name}{i.options.length ? <span className="text-muted2"> · {i.options.join(", ")}</span> : ""}</li>)}</ul>
    {o.manual && <div className="mt-2 rounded-xl bg-ink px-3 py-2 text-center font-heading font-black text-white" data-testid={`pay-at-pickup-${o.number}`}>PAY AT PICKUP · {money(o.totals.total_cents)} <span className="text-xs font-semibold">incl. GST</span></div>}
    {o.manual && o.phone && <div className="mt-1 text-xs text-muted2">Phone: {o.phone}</div>}
    {o.ready_by && <div className="mt-1 text-xs font-semibold text-muted2" data-testid={`ready-by-${o.number}`}>Prepared by {o.ready_by}</div>}
    {o.manual ? <ManualActions o={o} act={act} /> :
    <div className="mt-3 flex gap-2">
      <button onClick={() => onAdvance(o)} data-testid={`advance-order-${o.number}`}
        className={`flex-1 rounded-full py-3 font-heading font-black ${o.status === "preparing" ? "bg-ready text-ink" : "bg-brand text-white"}`}>{action}</button>
      <button onClick={() => onCancel(o)} className="rounded-full border-2 border-line px-3 text-sm font-bold text-danger" data-testid={`cancel-order-${o.number}`}>Cancel</button>
    </div>}
  </div>
);

const CancelDialog = ({ order, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl" data-testid="cancel-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-black">Cancel & refund {order?.number}?</DialogTitle>
          <DialogDescription>The customer gets a full refund of {order && money(order.totals.total_cents)} including GST.</DialogDescription></DialogHeader>
        <Input placeholder="Reason (e.g. out of oat milk)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-input" />
        <button className="mt-2 w-full rounded-full bg-danger py-3 font-bold text-white disabled:opacity-50" disabled={reason.trim().length < 2} onClick={() => onConfirm(reason)} data-testid="confirm-cancel-btn">Cancel and refund</button>
      </DialogContent>
    </Dialog>
  );
};

const ManualDialog = ({ pending, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState("cash");
  const o = pending?.o;
  const isReject = pending?.kind === "reject";
  return (
    <Dialog open={!!pending} onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="rounded-3xl" data-testid="manual-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-black">{isReject ? `Reject ${o?.number}?` : `${o?.number}: paid and picked up`}</DialogTitle>
          <DialogDescription>{isReject ? "The customer sees your reason on their status page." : `Collect ${o && money(o.totals.total_cents)} including GST. How did they pay?`}</DialogDescription></DialogHeader>
        {isReject ? <Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" /> :
          <div className="grid gap-2">{PAY_METHODS.map(([k, l]) => <button key={k} onClick={() => setMethod(k)} data-testid={`pay-method-${k}`}
            className={`rounded-2xl border-2 p-3 text-left font-bold ${method === k ? "border-brand bg-brand-pale" : "border-line"}`}>{l}</button>)}</div>}
        <button className={`mt-2 w-full rounded-full py-3 font-bold text-white ${isReject ? "bg-danger" : "bg-brand"}`} onClick={() => onConfirm(isReject ? { reason } : { method })} data-testid="manual-confirm-btn">
          {isReject ? "Reject order" : "Confirm paid and picked up"}</button>
      </DialogContent>
    </Dialog>
  );
};

function stageSound(prev, all) {
  let pick = null;
  for (const o of all) {
    const p = prev.get(o.id);
    if (p === o.status || (p === undefined && !needsAck(o))) continue;
    const s = soundForTransition(p, o.status, "staff");
    if (s && (!pick || PRIORITY.indexOf(s) < PRIORITY.indexOf(pick))) pick = s;
  }
  return pick;
}

function useRepeatAlert(dataRef) {
  useEffect(() => {
    const t = setInterval(() => {
      const n = (dataRef.current?.active || []).filter(needsAck).length;
      if (n) { play("new_order"); toast.warning(`${n} new order${n > 1 ? "s" : ""} waiting — tap to acknowledge`); }
    }, 15000);
    return () => clearInterval(t);
  }, [dataRef]);
}

export default function OrderBoard({ api, onAuthFail, tick, connected }) {
  const [data, setData] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [pending, setPending] = useState(null);
  const prev = useRef(null);
  const dataRef = useRef(null);
  const load = useCallback(() => api.get("/board").then((r) => {
    const all = [...r.data.active, ...r.data.recent];
    if (prev.current) {
      const s = stageSound(prev.current, all);
      if (s) play(s);
      const fresh = r.data.active.filter((o) => !prev.current.has(o.id) && needsAck(o));
      if (fresh.length) toast.success(fresh.some((o) => o.status === "awaiting_approval") ? "New order needs approval!" : "New order!");
    }
    prev.current = new Map(all.map((o) => [o.id, o.status]));
    dataRef.current = r.data;
    setData(r.data);
  }).catch((e) => { if (e?.response?.status === 401) onAuthFail("Your staff access was turned off."); }), [api, onAuthFail]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tick]);
  useEffect(() => { const t = setInterval(load, connected ? 20000 : 3000); return () => clearInterval(t); }, [load, connected]);
  useRepeatAlert(dataRef);
  const ack = async (o) => { try { await api.post(`/orders/${o.id}/ack`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const advance = async (o) => { try { await api.post(`/orders/${o.id}/advance`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const post = async (o, path, body) => { try { await api.post(`/orders/${o.id}/${path}`, body); setPending(null); load(); } catch (e) { toast.error(errMsg(e)); } };
  const act = (o, kind) => {
    if (kind === "reject" || kind === "pickup") return setPending({ o, kind });
    if (kind === "no-show" && !window.confirm(`Mark ${o.number} as a no-show?`)) return;
    return kind === "advance" ? advance(o) : post(o, kind);
  };
  const cancel = async (reason) => {
    try { const r = await api.post(`/orders/${cancelling.id}/cancel`, { reason }); toast.success(`Refunded ${money(r.data.refund_amount_cents)}`); setCancelling(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <p className="text-muted2">Loading orders…</p>;
  const has = (st) => data.active.some((o) => o.status === st);
  const cols = COLS.filter(([s]) => !(s === "paid" && data.venue.manual && !has("paid")) && !(s === "awaiting_approval" && !data.venue.manual && !has("awaiting_approval")));
  return (
    <div data-testid="order-board">
      {data.venue.practice && <div className="mb-3 rounded-2xl border-2 border-dashed border-ink p-2 text-center text-sm font-black" data-testid="board-practice-banner">PRACTICE MODE — test-card orders for staff training. No real money.</div>}
      <div className={`grid gap-4 md:grid-cols-2 ${cols.length === 4 ? "xl:grid-cols-4" : "xl:grid-cols-3"}`}>
        {cols.map(([s, label, bg, action]) => {
          const list = data.active.filter((o) => o.status === s);
          return (
            <section key={s} className={`min-h-[40vh] rounded-3xl p-3 ${bg}`} data-testid={`board-col-${s}`}>
              <h2 className={`mb-3 flex items-center justify-between rounded-full px-4 py-2 font-heading text-lg font-black ${s === "ready" ? "bg-ready text-ink" : "bg-white"}`}>
                {label}<span data-testid={`board-count-${s}`}>{list.length}</span></h2>
              <div className="grid gap-3">{list.map((o) => <OrderCard key={o.id} o={o} action={action} onAdvance={advance} onCancel={setCancelling} act={act} onAck={ack} />)}</div>
            </section>);
        })}
      </div>
      {data.recent.length > 0 && <div className="mt-6"><div className="eyebrow mb-2">Recently done</div>
        <div className="flex flex-wrap gap-2">{data.recent.map((o) => <span key={o.id} className={`rounded-full px-3 py-1 text-sm font-bold ${o.status === "picked_up" ? "bg-gray-100" : "bg-danger-pale text-danger"}`}>{o.number} · {{ cancelled: "cancelled", rejected: "rejected", expired: "expired", no_show: "no-show" }[o.status] || "picked up"}</span>)}</div></div>}
      <CancelDialog key={cancelling?.id} order={cancelling} onClose={() => setCancelling(null)} onConfirm={cancel} />
      <ManualDialog key={pending ? `${pending.o.id}-${pending.kind}` : "none"} pending={pending} onClose={() => setPending(null)}
        onConfirm={(body) => post(pending.o, pending.kind, body)} />
    </div>
  );
}
